import tokenManager from "../utils/tokenManager";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const QUOTE_API_URL = process.env.REACT_APP_QUOTE_API_URL;

class QuoteSystemIntegrationService {
  /**
   * Establish system-to-system connection with Quote system
   */
  static async establishConnection() {
    try {
      const token = tokenManager.getAccessToken();
      const user = tokenManager.getUser();

      if (!token || !user) {
        throw new Error("Not authenticated");
      }

      if (!tokenManager.isTokenValid(token)) {
        try {
          await tokenManager.refreshAccessToken();
        } catch (refreshError) {
          throw new Error("Session expired. Please log in again.");
        }
      }

      const freshToken = tokenManager.getAccessToken();

      // company_id: prefer user object, fall back to decoding it from the JWT
      // (the token payload contains companyId directly)
      const decoded = tokenManager.decodeToken(freshToken);
      const companyId = user?.company?.id || decoded?.companyId;

      if (!companyId) {
        throw new Error("Could not resolve company ID from user or token");
      }

      const refreshToken = tokenManager.getRefreshToken();

      //   if (!refreshToken) {
      //     throw new Error(
      //       "No refresh token available. Please log out and log back in.",
      //     );
      //   }

      const response = await fetch(`${QUOTE_API_URL}/system/auth`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_id: companyId,
          api_url: API_BASE_URL,
          access_token: freshToken,
          refresh_token: freshToken,
          expires_in: 315360000,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Connection failed");
      }

      localStorage.setItem("quoteSystemConnected", "true");

      return result;
    } catch (error) {
      console.error("Quote system connection error:", error);
      throw error;
    }
  }

  /**
   * Get connection status
   */
  static async getConnectionStatus() {
    try {
      const token = tokenManager.getAccessToken();

      const response = await fetch(`${QUOTE_API_URL}/system/status`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Get status error:", error);
      throw error;
    }
  }

  /**
   * Test connection
   */
  static async testConnection() {
    try {
      const token = tokenManager.getAccessToken();

      const response = await fetch(`${QUOTE_API_URL}/system/test-connection`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Test connection error:", error);
      throw error;
    }
  }

  /**
   * Disconnect
   */
  static async disconnect() {
    try {
      const token = tokenManager.getAccessToken();

      const response = await fetch(`${QUOTE_API_URL}/system/disconnect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const result = await response.json();

      if (result.success) {
        localStorage.removeItem("quoteSystemConnected");
      }

      return result;
    } catch (error) {
      console.error("Disconnect error:", error);
      throw error;
    }
  }

  /**
   * Check if connected
   */
  static isConnected() {
    if (!tokenManager.isAuthenticated()) {
      localStorage.removeItem("quoteSystemConnected");
      return false;
    }

    return localStorage.getItem("quoteSystemConnected") === "true";
  }
}

export default QuoteSystemIntegrationService;
